import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type AppConfig, type HistoryItem, type MenuView, type ReadingResponse, type UserView } from "./api";
import { Logo } from "./icons";
import { bindBackButton, getInitData, haptic, openTelegramLink } from "./platform";
import { Birth } from "./screens/Birth";
import { Calc } from "./screens/Calc";
import { Calculators } from "./screens/Calculators";
import { Chat } from "./screens/Chat";
import { Favorites } from "./screens/Favorites";
import { Forecast } from "./screens/Forecast";
import { Home, type ForecastPeriod } from "./screens/Home";
import { MyReadings } from "./screens/MyReadings";
import { Paywall } from "./screens/Paywall";
import { Profile } from "./screens/Profile";
import { Referral } from "./screens/Referral";
import { Shop } from "./screens/Shop";
import { Palm } from "./screens/Palm";
import { Natal } from "./screens/Natal";
import { ResultScreen, type Loader } from "./screens/Result";
import { SpreadFlow, Spreads, type SpreadKind } from "./screens/Spreads";
import { Tarot78Flow, Tarot78, type Tarot78Kind } from "./screens/Tarot78";
import { Spinner, useOnline, useToast } from "./ui";

type Tab = "home" | "myReadings" | "favorites" | "chat" | "calc" | "spreads" | "calculators" | "tarot78";
type Boot = { kind: "loading" } | { kind: "error"; code: string; text?: string } | { kind: "ready"; menu: MenuView };
interface ResultState {
  title: string;
  moduleId: string;
  load: Loader;
}

/** Нижняя навигация — пять пунктов по мастер-промпту «Код Масштаба», иконки — золотые ассеты из референса. «Профиль» открывает модальный экран, а не отдельную вкладку. */
const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "home", label: "Главная", icon: "/assets/icons/nav-home.webp" },
  { id: "myReadings", label: "Мои разборы", icon: "/assets/icons/nav-readings.webp" },
  { id: "favorites", label: "Избранное", icon: "/assets/icons/nav-favorites.webp" },
  { id: "chat", label: "Спросить", icon: "/assets/icons/nav-chat.webp" },
  { id: "calculators", label: "Калькуляторы", icon: "" },
];

export function App() {
  const [boot, setBoot] = useState<Boot>({ kind: "loading" });
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [tab, setTab] = useState<Tab>("home");
  const [calcKind, setCalcKind] = useState("star");
  const [forecast, setForecast] = useState<ForecastPeriod | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);
  const [spread, setSpread] = useState<SpreadKind | null>(null);
  const [tarot78, setTarot78] = useState<Tarot78Kind | null>(null);
  const [paywall, setPaywall] = useState(false);
  const [profile, setProfile] = useState(false);
  const [referral, setReferral] = useState(false);
  const [shop, setShop] = useState(false);
  const [palm, setPalm] = useState(false);
  const [natal, setNatal] = useState(false);
  const [calcAutoOpen, setCalcAutoOpen] = useState<string | null>(null);
  const [toast, showToast] = useToast();
  const [history, setHistory] = useState<HistoryItem[] | null>(null);
  const [historyFailed, setHistoryFailed] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      setHistory((await api.history()).items);
      setHistoryFailed(false);
    } catch {
      setHistoryFailed(true);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const menu = await api.me();
      setBoot({ kind: "ready", menu });
    } catch (e) {
      setBoot({ kind: "error", code: e instanceof ApiError ? e.code : "network", ...(e instanceof ApiError && e.text ? { text: e.text } : {}) });
    }
  }, []);

  useEffect(() => {
    if (!getInitData()) {
      setBoot({ kind: "error", code: "no_telegram" });
      return;
    }
    void refresh();
    api.config().then(setConfig, () => undefined);
  }, [refresh]);

  const ready = boot.kind === "ready" && boot.menu.user.onboarded;
  useEffect(() => {
    if (ready) void loadHistory();
  }, [ready, loadHistory]);

  // Связь: при возвращении сети сами догружаем то, что не загрузилось.
  const online = useOnline();
  const bootNetworkError = boot.kind === "error" && boot.code === "network";
  useEffect(() => {
    if (!online) return;
    if (bootNetworkError) void refresh();
    else if (ready && (history === null || historyFailed)) void loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  // Системная кнопка «Назад» Telegram: закрывает верхний слой, затем ведёт на главную; на главной скрыта.
  const canGoBack = paywall || referral || shop || palm || natal || profile || spread !== null || tarot78 !== null || result !== null || forecast !== null || tab !== "home";
  const goBackRef = useRef<() => void>(() => undefined);
  goBackRef.current = () => {
    if (paywall) setPaywall(false);
    else if (palm) setPalm(false);
    else if (natal) setNatal(false);
    else if (shop) setShop(false);
    else if (referral) setReferral(false);
    else if (profile) setProfile(false);
    else if (spread) setSpread(null);
    else if (tarot78) setTarot78(null);
    else if (result) setResult(null);
    else if (forecast) setForecast(null);
    else setTab("home");
  };
  useEffect(() => (canGoBack ? bindBackButton(() => goBackRef.current()) : undefined), [canGoBack]);

  const setUser = (user: UserView) => setBoot((b) => (b.kind === "ready" ? { kind: "ready", menu: { ...b.menu, user } } : b));

  if (boot.kind === "loading") {
    return (
      <div className="app">
        <div className="warm center">
          <Spinner />
        </div>
      </div>
    );
  }
  // Ещё не регистрировался в боте: онбординг (имя/дата/вопросы) сам зарегистрирует клиента на первом шаге,
  // отдельного шага «согласие» не нужно — Telegram сам показывает его при первом открытии Mini App.
  if (boot.kind === "error" && boot.code === "not_registered") {
    return (
      <div className="app">
        <div className="scroll warm">
          <Birth
            onDone={() => {
              haptic("success");
              void refresh();
            }}
          />
        </div>
      </div>
    );
  }
  if (boot.kind === "error") return <Problem code={boot.code} text={boot.text} config={config} retry={() => void refresh()} />;

  const { user } = boot.menu;

  if (!user.onboarded) {
    return (
      <div className="app">
        <div className="scroll warm">
          <Birth
            onDone={() => {
              haptic("success");
              void refresh();
            }}
          />
        </div>
      </div>
    );
  }

  const onStreak = (r: ReadingResponse) => {
    if (r.coinsAwarded > 0) void refresh();
  };
  const openPaywall = () => setPaywall(true);
  const toggleFavorite = async (moduleId: string, on: boolean) => {
    try {
      setHistory((await api.setFavorite(moduleId, on)).items);
    } catch {
      showToast("Не удалось сохранить. Попробуйте ещё раз.");
    }
  };
  const openHistoryItem = (i: HistoryItem) => setResult({ title: i.title, moduleId: i.moduleId, load: () => api.reading(i.moduleId) });
  const historyProps = {
    items: history,
    failed: historyFailed,
    onRetry: () => void loadHistory(),
    onOpen: openHistoryItem,
    onToggleFavorite: (i: HistoryItem) => void toggleFavorite(i.moduleId, !i.favorite),
  };
  const openedItem = result ? history?.find((i) => i.moduleId === result.moduleId) : undefined;
  const openCalc = (kindId: string) => {
    setCalcKind(kindId);
    setTab("calc");
  };
  const onCard = (id: string) => {
    if (id === "reading") return openCalc("star");
    if (id === "compat") return openCalc("compat");
    if (id === "money") return openCalc("money");
    if (id === "forecast") return setForecast("today");
    if (id === "tarot") return setTab("spreads");
    if (id === "tarot78") return setTab("tarot78");
    if (id === "energy") return showToast("Энергия и здоровье — раздел скоро появится");
    if (id === "palm") return setPalm(true);
    if (id === "natal") return setNatal(true);
    if (id === "chat") return setTab("chat");
    if (id === "matrix") {
      setCalcAutoOpen("matrix");
      setTab("calculators");
      return;
    }
  };

  return (
    <div className="app">
      {!online && (
        <div className="offlinebar" role="status">
          Нет соединения — данные обновятся, когда сеть вернётся
        </div>
      )}
      <div className="scroll">
        {tab === "home" && (
          <Home
            user={user}
            onProfile={() => setProfile(true)}
            onCard={onCard}
            onGift={() => openCalc("star")}
            onDayLoaded={onStreak}
            onOpenDayFull={() =>
              setResult({ title: "Число дня", moduleId: "day_number", load: () => api.reading("day_number") })
            }
          />
        )}
        {tab === "myReadings" && <MyReadings {...historyProps} />}
        {tab === "favorites" && <Favorites {...historyProps} />}
        {tab === "chat" && <Chat onPaywall={openPaywall} />}
        {tab === "calc" && (
          <Calc user={user} initial={calcKind} onResult={(title, moduleId, load) => setResult({ title, moduleId, load })} />
        )}
        {tab === "spreads" && <Spreads user={user} onStart={setSpread} onPaywall={openPaywall} />}
        {tab === "tarot78" && <Tarot78 user={user} onStart={setTarot78} onPaywall={openPaywall} />}
        {tab === "calculators" && (
          <Calculators user={user} autoOpenId={calcAutoOpen} onAutoOpened={() => setCalcAutoOpen(null)} />
        )}
      </div>

      <nav className="nav" aria-label="Разделы">
        {TABS.map((t) => (
          <button key={t.id} className={tab === t.id ? "on" : ""} onClick={() => setTab(t.id)} aria-label={t.label}>
            {t.icon ? <img className="navicon" src={t.icon} alt="" /> : <NavCalcIcon />}
            <span className="navlabel">{t.label}</span>
          </button>
        ))}
        <button className={profile ? "on" : ""} onClick={() => setProfile(true)} aria-label="Профиль">
          <img className="navicon" src="/assets/icons/nav-profile.webp" alt="" />
          <span className="navlabel">Профиль</span>
        </button>
      </nav>

      {forecast && <Forecast user={user} config={config} initial={forecast} onClose={() => setForecast(null)} onPaywall={openPaywall} />}
      {result && (
        <ResultScreen
          title={result.title}
          moduleId={result.moduleId}
          load={result.load}
          user={user}
          favorite={openedItem ? openedItem.favorite : null}
          onFavorite={() => openedItem && void toggleFavorite(openedItem.moduleId, !openedItem.favorite)}
          onClose={() => setResult(null)}
          onPaywall={openPaywall}
          aiExpandEnabled={boot.menu.aiExpandEnabled}
          aiReportEnabled={boot.menu.aiReportEnabled}
          onLoaded={(r) => {
            onStreak(r);
            void loadHistory();
          }}
        />
      )}
      {spread && <SpreadFlow kind={spread} onClose={() => setSpread(null)} onPaywall={openPaywall} onLoaded={onStreak} />}
      {tarot78 && <Tarot78Flow kind={tarot78} onClose={() => setTarot78(null)} onPaywall={openPaywall} onLoaded={onStreak} />}
      {profile && (
        <Profile
          user={user}
          config={config}
          birthDateChangeEnabled={boot.menu.birthDateChangeEnabled}
          onClose={() => setProfile(false)}
          onPaywall={openPaywall}
          onUser={setUser}
          onRefresh={() => void refresh()}
          onReferral={() => setReferral(true)}
          showToast={showToast}
        />
      )}
      {referral && (
        <Referral config={config} onClose={() => setReferral(false)} showToast={showToast} onShop={() => setShop(true)} />
      )}
      {shop && <Shop onClose={() => setShop(false)} showToast={showToast} />}
      {palm && <Palm user={user} onClose={() => setPalm(false)} onPaywall={openPaywall} />}
      {natal && <Natal user={user} onClose={() => setNatal(false)} onPaywall={openPaywall} />}
      {paywall && (
        <Paywall
          onClose={() => setPaywall(false)}
          onPaid={async () => {
            await refresh();
            setResult(null);
            setSpread(null);
            setTarot78(null);
          }}
          showToast={showToast}
        />
      )}
      {toast}
    </div>
  );
}

/** Иконка калькуляторов: рисуется кодом, в наборе золотых ассетов её нет. */
function NavCalcIcon() {
  return (
    <svg className="navicon navsvg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4.5" y="3" width="15" height="18" rx="2.5" />
      <rect x="7.5" y="6" width="9" height="3.5" rx="0.8" />
      <path d="M8 13h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01M16 17h.01" />
    </svg>
  );
}

function Problem({ code, text, config, retry }: { code: string; text?: string | undefined; config: AppConfig | null; retry: () => void }) {
  if (code === "maintenance") {
    return (
      <div className="app">
        <div className="warm center">
          <Logo />
          <h1>Технические работы</h1>
          <p>{text ?? "Скоро всё заработает — загляните позже."}</p>
          <button className="outline" style={{ marginTop: 16 }} onClick={retry}>
            Проверить снова
          </button>
        </div>
      </div>
    );
  }
  if (code === "closed_beta") {
    return (
      <div className="app">
        <div className="warm center">
          <Logo />
          <h1>Закрытое тестирование</h1>
          <p>Сервис пока работает по приглашениям. Мы сообщим, когда он откроется для всех.</p>
        </div>
      </div>
    );
  }
  const notRegistered = code === "not_registered" || code === "no_telegram";
  const bot = config?.botUsername;
  return (
    <div className="app">
      <div className="warm center">
        <Logo />
        {notRegistered ? (
          <>
            <h1>Начните с бота</h1>
            <p>Откройте бота, нажмите /start, выберите страну и согласитесь с условиями — после этого приложение заработает.</p>
            {bot && (
              <button className="primary" onClick={() => openTelegramLink(`https://t.me/${bot}`)}>
                Открыть бота
              </button>
            )}
          </>
        ) : (
          <>
            <h1>Что-то пошло не так</h1>
            <p>{code === "unauthorized" ? "Откройте приложение заново из бота." : "Сервис временно недоступен. Попробуйте ещё раз."}</p>
            <button className="primary" onClick={retry}>
              Повторить
            </button>
          </>
        )}
      </div>
    </div>
  );
}
